import { createSeriesMarkers, LineSeries as LineSeriesDefinition } from 'lightweight-charts'
import React, { memo, useEffect, useRef } from 'react'
import { Box } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import {
    ColorType,
    CrosshairMode,
    ISeriesApi,
    LineStyle,
    MouseEventParams,
    Time,
    UTCTimestamp,
    createChart
} from 'lightweight-charts'
import {
    SEASONAL_AVERAGE_COLOR,
    SeasonalPoint,
    SeasonalYearSeries,
    SeasonalsMode,
    formatSeasonalDayMonth,
    formatSeasonalMonth,
    formatSeasonalValue
} from '../../util/seasonals'

type SeasonalsChartProps = {
    series: SeasonalYearSeries[]
    averageData?: SeasonalPoint[]
    mode?: SeasonalsMode
    height?: number | string
    /** Full-page: show year + last value on the right axis (same as compare-chart series title). */
    showRightLabels?: boolean
}

type TrackedSeries = {
    api: ISeriesApi<'Line'>
    year: number
}

const SeasonalsChart = ({
    series,
    averageData,
    mode = 'percent',
    height = 200,
    showRightLabels = false
}: SeasonalsChartProps) => {
    const theme = useTheme()
    const containerRef = useRef<HTMLDivElement>(null)
    const tooltipRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const container = containerRef.current
        const tooltip = tooltipRef.current
        if (!container) return

        const priceFormatter = (price: number) => formatSeasonalValue(price, mode)

        const chart = createChart(container, {
            autoSize: true,
            layout: {
                background: { type: ColorType.Solid, color: 'transparent' },
                textColor: theme.palette.text.secondary,
                fontSize: 10,
                fontFamily: theme.typography.fontFamily
            },
            grid: {
                vertLines: {
                    visible: true,
                    style: LineStyle.Dashed,
                    color: alpha(theme.palette.text.secondary, 0.12)
                },
                horzLines: { visible: false }
            },
            rightPriceScale: {
                visible: showRightLabels,
                borderVisible: false,
                scaleMargins: { top: 0.1, bottom: 0.1 }
            },
            leftPriceScale: { visible: false },
            handleScroll: false,
            handleScale: false,
            crosshair: {
                mode: CrosshairMode.Magnet,
                vertLine: {
                    labelVisible: false,
                    style: LineStyle.Solid,
                    width: 1,
                    color: alpha(theme.palette.text.secondary, 0.4)
                },
                horzLine: { labelVisible: false, visible: false }
            },
            timeScale: {
                borderVisible: false,
                fixLeftEdge: true,
                fixRightEdge: true,
                lockVisibleTimeRangeOnResize: true,
                tickMarkFormatter: (time: Time) => formatSeasonalMonth(time as UTCTimestamp)
            },
            localization: {
                timeFormatter: (time: Time) => formatSeasonalMonth(time as UTCTimestamp),
                priceFormatter
            }
        })

        const tracked: TrackedSeries[] = []
        let baselineOwner: ISeriesApi<'Line'> | null = null

        // Render oldest year first so the most recent (current) year draws on top.
        const ordered = [...series].reverse()
        ordered.forEach((yearSeries) => {
            const lineSeries = chart.addSeries(LineSeriesDefinition, {
                color: yearSeries.color,
                lineWidth: yearSeries.isCurrent ? 3 : 2,
                priceLineVisible: false,
                // Same pattern as compare securities: title + lastValueVisible → "2014 60.37%" on the axis.
                title: showRightLabels ? String(yearSeries.year) : '',
                lastValueVisible: showRightLabels,
                crosshairMarkerVisible: true,
                crosshairMarkerRadius: 3,
                priceFormat: { type: 'custom', formatter: priceFormatter }
            })
            lineSeries.setData(yearSeries.data)

            if (!baselineOwner) baselineOwner = lineSeries

            if (yearSeries.data.length > 0) {
                tracked.push({ api: lineSeries, year: yearSeries.year })

                if (yearSeries.isCurrent) {
                    const lastPoint = yearSeries.data[yearSeries.data.length - 1]
                    createSeriesMarkers(lineSeries, [
                        {
                            time: lastPoint.time,
                            position: 'inBar',
                            color: yearSeries.color,
                            shape: 'circle',
                            size: 1
                        }
                    ])
                }
            }
        })

        if (averageData && averageData.length > 0) {
            const averageSeries = chart.addSeries(LineSeriesDefinition, {
                color: SEASONAL_AVERAGE_COLOR,
                lineWidth: 2,
                lineStyle: LineStyle.Dashed,
                priceLineVisible: false,
                lastValueVisible: false,
                crosshairMarkerVisible: true,
                crosshairMarkerRadius: 3,
                priceFormat: { type: 'custom', formatter: priceFormatter }
            })
            averageSeries.setData(averageData)
        }

        if (baselineOwner && mode === 'percent') {
            baselineOwner.createPriceLine({
                price: 0,
                color: alpha(theme.palette.text.primary, 0.35),
                lineWidth: 1,
                lineStyle: LineStyle.Solid,
                axisLabelVisible: false,
                title: ''
            })
        }

        const handleCrosshairMove = (param: MouseEventParams) => {
            if (!tooltip) return
            if (!param.time || !param.point || param.point.x < 0 || param.point.y < 0) {
                tooltip.style.display = 'none'
                return
            }

            const rows = series
                .map((s) => {
                    const api = tracked.find((t) => t.year === s.year)?.api
                    if (!api) return null
                    const found = param.seriesData.get(api) as { value?: number } | undefined
                    if (!found || found.value === undefined) return null
                    return { year: s.year, color: s.color, value: found.value }
                })
                .filter((r): r is { year: number; color: string; value: number } => r !== null)

            if (rows.length === 0) {
                tooltip.style.display = 'none'
                return
            }

            const dateLabel = formatSeasonalDayMonth(param.time as UTCTimestamp)
            tooltip.innerHTML = `
                <div class="seasonals-tooltip-date">${dateLabel}</div>
                ${rows
                    .map(
                        (row) => `
                    <div class="seasonals-tooltip-row">
                        <span class="seasonals-tooltip-dot" style="background:${row.color}"></span>
                        <span class="seasonals-tooltip-year">${row.year}</span>
                        <span class="seasonals-tooltip-value">${formatSeasonalValue(row.value, mode)}</span>
                    </div>`
                    )
                    .join('')}
            `
            tooltip.style.display = 'block'

            const tooltipWidth = tooltip.offsetWidth
            const tooltipHeight = tooltip.offsetHeight
            const containerWidth = container.clientWidth
            const containerHeight = container.clientHeight
            const margin = 12

            let left = param.point.x + margin
            if (left + tooltipWidth > containerWidth) {
                left = param.point.x - margin - tooltipWidth
            }
            left = Math.max(0, Math.min(left, containerWidth - tooltipWidth))

            let top = param.point.y + margin
            if (top + tooltipHeight > containerHeight) {
                top = Math.max(0, containerHeight - tooltipHeight)
            }

            tooltip.style.left = `${left}px`
            tooltip.style.top = `${top}px`
        }

        chart.subscribeCrosshairMove(handleCrosshairMove)
        chart.timeScale().fitContent()

        return () => {
            chart.unsubscribeCrosshairMove(handleCrosshairMove)
            chart.remove()
        }
    }, [series, averageData, mode, showRightLabels, theme])

    return (
        <Box
            ref={containerRef}
            sx={{
                position: 'relative',
                width: '100%',
                height,
                '& .seasonals-tooltip': {
                    position: 'absolute',
                    zIndex: 3,
                    display: 'none',
                    pointerEvents: 'none',
                    padding: '6px 8px',
                    borderRadius: 1,
                    backgroundColor: alpha(theme.palette.background.paper, 0.96),
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: theme.shadows[3],
                    fontFamily: theme.typography.fontFamily
                },
                '& .seasonals-tooltip-date': {
                    fontSize: '11px',
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    marginBottom: '4px'
                },
                '& .seasonals-tooltip-row': {
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    lineHeight: 1.5
                },
                '& .seasonals-tooltip-dot': {
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    flexShrink: 0
                },
                '& .seasonals-tooltip-year': {
                    fontSize: '11px',
                    color: theme.palette.text.secondary,
                    minWidth: '30px'
                },
                '& .seasonals-tooltip-value': {
                    fontSize: '11px',
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    marginLeft: 'auto',
                    fontVariantNumeric: 'tabular-nums'
                }
            }}
        >
            <div ref={tooltipRef} className="seasonals-tooltip" />
        </Box>
    )
}

export default memo(SeasonalsChart)
